import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  TrendingUp, Wallet, Package, ArrowLeft, Calendar,
  DollarSign, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import api from '../../utils/api';

const OperatorEarnings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [earnings, setEarnings] = useState(null);

  useEffect(() => {
    fetchEarnings();
  }, []);

  const fetchEarnings = async () => {
    setLoading(true);
    try {
      const response = await api.get('/operators/earnings');
      setEarnings(response.data.earnings);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load earnings',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white p-6">
        <button 
          onClick={() => navigate('/operator/dashboard')}
          className="flex items-center gap-2 text-green-100 hover:text-white mb-3"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <TrendingUp className="h-6 w-6" />
          Earnings
        </h1>
      </div>

      <div className="p-4 space-y-4">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 gap-3">
          <Card className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
            <Wallet className="h-6 w-6 text-green-600 mb-2" />
            <p className="text-sm text-gray-600">Available Balance</p>
            <p className="text-2xl font-bold text-green-700">
              K{earnings?.walletBalance?.toFixed(2) || '0.00'}
            </p>
          </Card>
          
          <Card className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
            <TrendingUp className="h-6 w-6 text-blue-600 mb-2" />
            <p className="text-sm text-gray-600">Total Earnings</p>
            <p className="text-2xl font-bold text-blue-700">
              K{earnings?.totalEarnings?.toFixed(2) || '0.00'}
            </p>
          </Card>
        </div>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <Package className="h-6 w-6 text-orange-500 mb-1" />
              <p className="text-sm text-gray-600">Total Deliveries</p>
            </div>
            <p className="text-3xl font-bold">{earnings?.totalDeliveries || 0}</p>
          </div>
        </Card>

        {/* Recent Earnings */}
        <div>
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <Calendar className="h-5 w-5 text-orange-500" />
            Recent Earnings
          </h2>

          {(!earnings?.recentJobs || earnings.recentJobs.length === 0) ? (
            <Card className="p-8 text-center">
              <DollarSign className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No earnings yet</p>
              <p className="text-sm text-gray-400">Complete deliveries to start earning</p>
            </Card>
          ) : (
            <div className="space-y-2">
              {earnings.recentJobs.map((job, idx) => (
                <Card key={idx} className="p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                        <ArrowUpRight className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">Delivery Completed</p>
                        <p className="text-xs text-gray-500">
                          {job.pickupAddress?.substring(0, 20)}... → {job.dropoffAddress?.substring(0, 20)}...
                        </p>
                        <p className="text-xs text-gray-400">
                          {formatDate(job.deliveredAt)}
                        </p>
                      </div>
                    </div>
                    <p className="text-lg font-bold text-green-600">
                      +K{job.price?.toFixed(2)}
                    </p>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Withdraw Button */}
        {earnings?.walletBalance > 0 && (
          <Button 
            className="w-full bg-green-500 hover:bg-green-600 py-6"
            onClick={() => toast({
              title: 'Coming Soon',
              description: 'Wallet withdrawal will be available soon'
            })}
          >
            <Wallet className="h-5 w-5 mr-2" />
            Withdraw K{earnings?.walletBalance?.toFixed(2)}
          </Button>
        )}
      </div>
    </div>
  );
};

export default OperatorEarnings;
